import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
import _extends from "@babel/runtime/helpers/extends";
import React from 'react';
import { FormattedMessage } from 'react-intl-next';
import isDecimal from 'validator/lib/isDecimal';
import isNumeric from 'validator/lib/isNumeric';
import { JSONTransformer } from '@atlaskit/editor-json-transformer';
import { WikiMarkupTransformer } from '@atlaskit/editor-wikimarkup-transformer';
import { fg } from '@atlaskit/platform-feature-flags';
import { convertDateFormatToSlashes } from '../../../jira-common/utils/CommonUtils';
import { FormQuestionType } from '../../models/Form';
import { ChoiceValidationErrorMsg, DateTimeValidationErrorMsg, IntlValidationErrorMessages, NumberValidationErrorMsg, TextValidationErrorMsg, UserPickerValidationErrorMsg, ValidationErrorMsg } from './validationErrorMessages.intl';
import { wordCounter } from './wordCounter';
var wikiTransformer = new WikiMarkupTransformer();
var jsonTransformer = new JSONTransformer();
export var getRichTextValidation = function getRichTextValidation(requirements, adf, questionType, fieldLabel) {
  var pmNode = jsonTransformer.parse(adf);
  return getTextValidation(requirements, wikiTransformer.encode(pmNode), questionType, fieldLabel);
};
export var getTextValidation = function getTextValidation(requirements, text, questionType, fieldLabel) {
  var trimmedText = text.trim();
  var wordCount;
  if (requirements.mnw || requirements.mxw) {
    wordCount = wordCounter(trimmedText);
  }

  // text is empty AND a response is required (requirements.rq)
  if (trimmedText.length === 0) {
    if (requirements.rq) {
      return fieldLabel && fg('a11y_error_messages') ? [{
        key: ValidationErrorMsg.RequiredWithLabel,
        message: /*#__PURE__*/React.createElement(FormattedMessage, _extends({}, IntlValidationErrorMessages[ValidationErrorMsg.RequiredWithLabel], {
          values: {
            fieldLabel: fieldLabel
          }
        }))
      }] : [{
        key: ValidationErrorMsg.Required,
        message: /*#__PURE__*/React.createElement(FormattedMessage, IntlValidationErrorMessages[ValidationErrorMsg.Required])
      }];
    }
  } else {
    // the char count in the trimmed text does not match the char count in the validation rule
    if (requirements.mnc && requirements.mnc === requirements.mxc && trimmedText.length !== requirements.mnc) {
      return [{
        key: TextValidationErrorMsg.ExactChars,
        message: /*#__PURE__*/React.createElement(FormattedMessage, _extends({}, IntlValidationErrorMessages[TextValidationErrorMsg.ExactChars], {
          values: {
            mnc: requirements.mnc
          }
        }))
      }];
    }

    // the word count in the trimmed text does not match the word count in the validation rule
    if (requirements.mnw && requirements.mnw === requirements.mxw && wordCount !== requirements.mnw) {
      return [{
        key: TextValidationErrorMsg.ExactWords,
        message: /*#__PURE__*/React.createElement(FormattedMessage, _extends({}, IntlValidationErrorMessages[TextValidationErrorMsg.ExactWords], {
          values: {
            mnw: requirements.mnw
          }
        }))
      }];
    }

    // the char count in the trimmed text is outside the [min,max] range in the validation rule
    if (requirements.mnc && requirements.mxc && (trimmedText.length < requirements.mnc || trimmedText.length > requirements.mxc)) {
      return [{
        key: TextValidationErrorMsg.BetweenChars,
        message: /*#__PURE__*/React.createElement(FormattedMessage, _extends({}, IntlValidationErrorMessages[TextValidationErrorMsg.BetweenChars], {
          values: {
            mnc: requirements.mnc,
            mxc: requirements.mxc
          }
        }))
      }];
    }

    // the word count in the trimmed text is outside the [min,max] range in the validation rule
    if (requirements.mnw && requirements.mxw && wordCount && (wordCount < requirements.mnw || wordCount > requirements.mxw)) {
      return [{
        key: TextValidationErrorMsg.BetweenWords,
        message: /*#__PURE__*/React.createElement(FormattedMessage, _extends({}, IntlValidationErrorMessages[TextValidationErrorMsg.BetweenWords], {
          values: {
            mnw: requirements.mnw,
            mxw: requirements.mxw
          }
        }))
      }];
    }

    // the char count in the trimmed text is below the min value in the validation rule
    if (requirements.mnc && trimmedText.length < requirements.mnc) {
      return [{
        key: TextValidationErrorMsg.MinChars,
        message: /*#__PURE__*/React.createElement(FormattedMessage, _extends({}, IntlValidationErrorMessages[TextValidationErrorMsg.MinChars], {
          values: {
            mnc: requirements.mnc - 1
          }
        }))
      }];
    } // Note: threshold is modified here to fit the validation error messages wording see (PF-1683)

    // the word count in the trimmed text is below the min value in the validation rule
    if (requirements.mnw && wordCount && wordCount < requirements.mnw) {
      return [{
        key: TextValidationErrorMsg.MinWords,
        message: /*#__PURE__*/React.createElement(FormattedMessage, _extends({}, IntlValidationErrorMessages[TextValidationErrorMsg.MinWords], {
          values: {
            mnw: requirements.mnw - 1
          }
        }))
      }];
    } // Note: threshold is modified here to fit the validation error messages wording see (PF-1683)

    // the char count in the trimmed text is above the max value in the validation rule
    if (requirements.mxc && trimmedText.length > requirements.mxc) {
      return [{
        key: TextValidationErrorMsg.MaxChars,
        message: /*#__PURE__*/React.createElement(FormattedMessage, _extends({}, IntlValidationErrorMessages[TextValidationErrorMsg.MaxChars], {
          values: {
            mxc: requirements.mxc + 1
          }
        }))
      }];
    } // Note: threshold is modified here to fit the validation error messages wording see (PF-1683)

    // the word count in the trimmed text is above the max value in the validation rule
    if (requirements.mxw && wordCount && wordCount > requirements.mxw) {
      return [{
        key: TextValidationErrorMsg.MaxWords,
        message: /*#__PURE__*/React.createElement(FormattedMessage, _extends({}, IntlValidationErrorMessages[TextValidationErrorMsg.MaxWords], {
          values: {
            mxw: requirements.mxw + 1
          }
        }))
      }];
    } // Note: threshold is modified here to fit the validation error messages wording see (PF-1683)

    // a custom regex pattern was provided
    if (requirements.rgx && requirements.rgx.p) {
      try {
        // don't use the trimmedText for regex validation
        if (!text.match(requirements.rgx.p)) {
          if (questionType === FormQuestionType.TextUrl) {
            return [{
              key: TextValidationErrorMsg.InvalidUrl,
              message: /*#__PURE__*/React.createElement(FormattedMessage, IntlValidationErrorMessages[TextValidationErrorMsg.InvalidUrl])
            }];
          }
          if (questionType === FormQuestionType.TextEmail) {
            return [{
              key: TextValidationErrorMsg.InvalidEmail,
              message: /*#__PURE__*/React.createElement(FormattedMessage, IntlValidationErrorMessages[TextValidationErrorMsg.InvalidEmail])
            }];
          }
          if (requirements.rgx.m) {
            return [{
              key: TextValidationErrorMsg.RegexCustomMsg,
              message: /*#__PURE__*/React.createElement(FormattedMessage, _extends({}, IntlValidationErrorMessages[TextValidationErrorMsg.RegexCustomMsg], {
                values: {
                  message: requirements.rgx.m
                }
              }))
            }];
          }
          return [{
            key: TextValidationErrorMsg.RegexDefaultMsg,
            message: /*#__PURE__*/React.createElement(FormattedMessage, IntlValidationErrorMessages[TextValidationErrorMsg.RegexDefaultMsg])
          }];
        }
      } catch (_unused) {
        // eslint-disable-next-line no-console
        console.log("Invalid regex: ".concat(requirements.rgx.p));
        return [{
          key: TextValidationErrorMsg.RegexInvalidMsg,
          message: /*#__PURE__*/React.createElement(FormattedMessage, IntlValidationErrorMessages[TextValidationErrorMsg.RegexInvalidMsg])
        }];
      }
    }
  }
};
export var getNumberValidation = function getNumberValidation(requirements, value, fieldLabel) {
  if (value.length > 0 && !(isNumeric(value) || isDecimal(value))) {
    return [{
      key: NumberValidationErrorMsg.InvalidNum,
      message: /*#__PURE__*/React.createElement(FormattedMessage, IntlValidationErrorMessages[NumberValidationErrorMsg.InvalidNum])
    }];
  }

  // Remove number decorations
  var cleanNumber = value.replace(/[\,]/g, '');
  var number = parseFloat(cleanNumber);
  if (!value.length) {
    if (requirements.rq && value.length === 0) {
      return fieldLabel && fg('a11y_error_messages') ? [{
        key: ValidationErrorMsg.RequiredWithLabel,
        message: /*#__PURE__*/React.createElement(FormattedMessage, _extends({}, IntlValidationErrorMessages[ValidationErrorMsg.RequiredWithLabel], {
          values: {
            fieldLabel: fieldLabel
          }
        }))
      }] : [{
        key: ValidationErrorMsg.Required,
        message: /*#__PURE__*/React.createElement(FormattedMessage, IntlValidationErrorMessages[ValidationErrorMsg.Required])
      }];
    }
  } else {
    if (isNaN(number)) {
      return [{
        key: NumberValidationErrorMsg.InvalidNum,
        message: /*#__PURE__*/React.createElement(FormattedMessage, IntlValidationErrorMessages[NumberValidationErrorMsg.InvalidNum])
      }];
    }
    if (requirements.wh && !Number.isInteger(number)) {
      return [{
        key: NumberValidationErrorMsg.WholeNum,
        message: /*#__PURE__*/React.createElement(FormattedMessage, IntlValidationErrorMessages[NumberValidationErrorMsg.WholeNum])
      }];
    }

    // Check equal
    if (requirements.mnn && requirements.mnn === requirements.mxn && number !== requirements.mnn) {
      return [{
        key: NumberValidationErrorMsg.ExactNum,
        message: /*#__PURE__*/React.createElement(FormattedMessage, _extends({}, IntlValidationErrorMessages[NumberValidationErrorMsg.ExactNum], {
          values: {
            mnn: requirements.mnn
          }
        }))
      }];
    }

    // check between
    if (requirements.mnn && requirements.mxn && (number < requirements.mnn || number > requirements.mxn)) {
      return [{
        key: NumberValidationErrorMsg.BetweenNum,
        message: /*#__PURE__*/React.createElement(FormattedMessage, _extends({}, IntlValidationErrorMessages[NumberValidationErrorMsg.BetweenNum], {
          values: {
            mnn: requirements.mnn,
            mxn: requirements.mxn
          }
        }))
      }];
    }
    if (requirements.mnn && number < requirements.mnn) {
      return [{
        key: NumberValidationErrorMsg.MinNum,
        message: /*#__PURE__*/React.createElement(FormattedMessage, _extends({}, IntlValidationErrorMessages[NumberValidationErrorMsg.MinNum], {
          values: {
            mnn: requirements.mnn
          }
        }))
      }];
    }
    if (requirements.mxn && number > requirements.mxn) {
      return [{
        key: NumberValidationErrorMsg.MaxNum,
        message: /*#__PURE__*/React.createElement(FormattedMessage, _extends({}, IntlValidationErrorMessages[NumberValidationErrorMsg.MaxNum], {
          values: {
            mxn: requirements.mxn
          }
        }))
      }];
    }
  }
};
export var getDateValidation = function getDateValidation(requirements, dateValue, fieldLabel) {
  // Date values to be reused throughout the validation logic
  var minDate;
  var maxDate;
  var enteredDate;
  if (requirements.mnd) {
    minDate = new Date(convertDateFormatToSlashes(requirements.mnd));
  }
  if (requirements.mxd) {
    maxDate = new Date(convertDateFormatToSlashes(requirements.mxd));
  }

  // Boolean values for validation logic
  var hasExactDateRequirements = requirements.mnd && requirements.mnd === requirements.mxd;
  var hasBetweenDateRequirements = !!requirements.mnd && !!requirements.mxd;

  // Check if empty
  if (!dateValue && requirements.rq) {
    return fieldLabel && fg('a11y_error_messages') ? [{
      key: ValidationErrorMsg.RequiredWithLabel,
      message: /*#__PURE__*/React.createElement(FormattedMessage, _extends({}, IntlValidationErrorMessages[ValidationErrorMsg.RequiredWithLabel], {
        values: {
          fieldLabel: fieldLabel
        }
      }))
    }] : [{
      key: ValidationErrorMsg.Required,
      message: /*#__PURE__*/React.createElement(FormattedMessage, IntlValidationErrorMessages[ValidationErrorMsg.Required])
    }];
  } else {
    enteredDate = new Date(convertDateFormatToSlashes(dateValue));
    var validationErrors = [];
    if (hasExactDateRequirements && minDate) {
      if (enteredDate.valueOf() !== minDate.valueOf()) {
        validationErrors.push({
          key: DateTimeValidationErrorMsg.ExactDate,
          message: /*#__PURE__*/React.createElement(FormattedMessage, _extends({}, IntlValidationErrorMessages[DateTimeValidationErrorMsg.ExactDate], {
            values: {
              mnd: minDate
            }
          }))
        });
      } // No need to compare with both max and min again
    } else if (hasBetweenDateRequirements && minDate && maxDate) {
      // Between dates scenario
      if (enteredDate < minDate || enteredDate > maxDate) {
        validationErrors.push({
          key: DateTimeValidationErrorMsg.BetweenDate,
          message: /*#__PURE__*/React.createElement(FormattedMessage, _extends({}, IntlValidationErrorMessages[DateTimeValidationErrorMsg.BetweenDate], {
            values: {
              mnd: minDate,
              mxd: maxDate
            }
          }))
        });
      }
    } else {
      if (requirements.mnd && minDate) {
        if (enteredDate < minDate) {
          validationErrors.push({
            key: DateTimeValidationErrorMsg.MinDate,
            message: /*#__PURE__*/React.createElement(FormattedMessage, _extends({}, IntlValidationErrorMessages[DateTimeValidationErrorMsg.MinDate], {
              values: {
                mnd: minDate
              }
            }))
          });
        }
      }
      if (requirements.mxd && maxDate) {
        if (enteredDate > maxDate) {
          validationErrors.push({
            key: DateTimeValidationErrorMsg.MaxDate,
            message: /*#__PURE__*/React.createElement(FormattedMessage, _extends({}, IntlValidationErrorMessages[DateTimeValidationErrorMsg.MaxDate], {
              values: {
                mxd: maxDate
              }
            }))
          });
        }
      }
    }
    if (validationErrors.length > 0) {
      return validationErrors;
    }
  }
};
export var getTimeValidation = function getTimeValidation(requirements, timeValue, fieldLabel) {
  if (!timeValue && requirements.rq) {
    return fieldLabel && fg('a11y_error_messages') ? [{
      key: ValidationErrorMsg.RequiredWithLabel,
      message: /*#__PURE__*/React.createElement(FormattedMessage, _extends({}, IntlValidationErrorMessages[ValidationErrorMsg.RequiredWithLabel], {
        values: {
          fieldLabel: fieldLabel
        }
      }))
    }] : [{
      key: ValidationErrorMsg.Required,
      message: /*#__PURE__*/React.createElement(FormattedMessage, IntlValidationErrorMessages[ValidationErrorMsg.Required])
    }];
  } else if (!!timeValue) {
    var time = new Date("01/01/1999 ".concat(timeValue)); // Add arbitrary fixed date to allow Date() object creation
    var validationErrors = [];
    if (isNaN(time.getTime())) {
      // Check time is valid
      validationErrors.push({
        key: DateTimeValidationErrorMsg.InvalidTime,
        message: /*#__PURE__*/React.createElement(FormattedMessage, IntlValidationErrorMessages[DateTimeValidationErrorMsg.InvalidTime])
      });
    } else if (requirements.mnt && requirements.mnt === requirements.mxt) {
      // Exact time scenario
      var minTime = new Date("01/01/1999 ".concat(requirements.mnt));
      if (time.getTime() !== minTime.getTime()) {
        validationErrors.push({
          key: DateTimeValidationErrorMsg.ExactTime,
          message: /*#__PURE__*/React.createElement(FormattedMessage, _extends({}, IntlValidationErrorMessages[DateTimeValidationErrorMsg.ExactTime], {
            values: {
              mnt: minTime
            }
          }))
        });
      }
    } else if (requirements.mnt && requirements.mxt) {
      // Between times scenario
      var _minTime = new Date("01/01/1999 ".concat(requirements.mnt));
      var maxTime = new Date("01/01/1999 ".concat(requirements.mxt));
      if (time < _minTime || time > maxTime) {
        validationErrors.push({
          key: DateTimeValidationErrorMsg.BetweenTime,
          message: /*#__PURE__*/React.createElement(FormattedMessage, _extends({}, IntlValidationErrorMessages[DateTimeValidationErrorMsg.BetweenTime], {
            values: {
              mnt: _minTime,
              mxt: maxTime
            }
          }))
        });
      }
    } else {
      if (requirements.mnt) {
        var _minTime2 = new Date("01/01/1999 ".concat(requirements.mnt));
        if (time < _minTime2) {
          validationErrors.push({
            key: DateTimeValidationErrorMsg.MinTime,
            message: /*#__PURE__*/React.createElement(FormattedMessage, _extends({}, IntlValidationErrorMessages[DateTimeValidationErrorMsg.MinTime], {
              values: {
                mnt: _minTime2
              }
            }))
          });
        }
      }
      if (requirements.mxt) {
        var _maxTime = new Date("01/01/1999 ".concat(requirements.mxt));
        if (time > _maxTime) {
          validationErrors.push({
            key: DateTimeValidationErrorMsg.MaxTime,
            message: /*#__PURE__*/React.createElement(FormattedMessage, _extends({}, IntlValidationErrorMessages[DateTimeValidationErrorMsg.MaxTime], {
              values: {
                mxt: _maxTime
              }
            }))
          });
        }
      }
    }
    if (validationErrors.length > 0) {
      return validationErrors;
    }
  }
};
export var getDateTimeValidation = function getDateTimeValidation(requirements, dateTime, fieldLabel) {
  // DateTime values to be reused throughout the validation logic
  var minDateTime;
  var maxDateTime;
  var enteredDateTime;
  if (requirements.mnd && requirements.mnt) {
    minDateTime = new Date("".concat(convertDateFormatToSlashes(requirements.mnd), " ").concat(requirements.mnt));
  }
  if (requirements.mxd && requirements.mxt) {
    maxDateTime = new Date("".concat(convertDateFormatToSlashes(requirements.mxd), " ").concat(requirements.mxt));
  }
  if (dateTime.date) {
    enteredDateTime = new Date("".concat(convertDateFormatToSlashes(dateTime.date), " ").concat(dateTime.time));
  }

  // Boolean values for validation logic
  var hasMinDateTimeRequirements = !!requirements.mnd && !!requirements.mnt;
  var hasMaxDateTimeRequirements = !!requirements.mxd && !!requirements.mxt;
  var hasExactDateTimeRequirements = hasMinDateTimeRequirements && requirements.mnd === requirements.mxd && requirements.mnt === requirements.mxt;
  var hasBetweenDateTimeRequirements = hasMinDateTimeRequirements && hasMaxDateTimeRequirements;

  // Date or time required
  if (requirements.rq && (!dateTime.date || !dateTime.time)) {
    return fieldLabel && fg('a11y_error_messages') ? [{
      key: DateTimeValidationErrorMsg.RequiredDateTimeWithLabel,
      message: /*#__PURE__*/React.createElement(FormattedMessage, _extends({}, IntlValidationErrorMessages[DateTimeValidationErrorMsg.RequiredDateTimeWithLabel], {
        values: {
          fieldLabel: fieldLabel
        }
      }))
    }] : [{
      key: DateTimeValidationErrorMsg.RequiredDateTime,
      message: /*#__PURE__*/React.createElement(FormattedMessage, IntlValidationErrorMessages[DateTimeValidationErrorMsg.RequiredDateTime])
    }];
  }
  if (hasExactDateTimeRequirements && minDateTime && enteredDateTime) {
    if (enteredDateTime.valueOf() !== minDateTime.valueOf()) {
      return [{
        key: DateTimeValidationErrorMsg.ExactDateTime,
        message: /*#__PURE__*/React.createElement(FormattedMessage, _extends({}, IntlValidationErrorMessages[DateTimeValidationErrorMsg.ExactDateTime], {
          values: {
            mnd: new Date(convertDateFormatToSlashes(requirements.mnd || '')),
            mnt: new Date("01/01/1999 ".concat(requirements.mnt))
          }
        }))
      }];
    }
  } else if (hasBetweenDateTimeRequirements && minDateTime && maxDateTime && enteredDateTime) {
    if (enteredDateTime < minDateTime || enteredDateTime > maxDateTime) {
      return [{
        key: DateTimeValidationErrorMsg.BetweenDateTime,
        message: /*#__PURE__*/React.createElement(FormattedMessage, _extends({}, IntlValidationErrorMessages[DateTimeValidationErrorMsg.BetweenDateTime], {
          values: {
            mnd: new Date(convertDateFormatToSlashes(requirements.mnd || '')),
            mnt: new Date("01/01/1999 ".concat(requirements.mnt)),
            mxd: new Date(convertDateFormatToSlashes(requirements.mxd || '')),
            mxt: new Date("01/01/1999 ".concat(requirements.mxt))
          }
        }))
      }];
    }
  } else if (hasMinDateTimeRequirements || hasMaxDateTimeRequirements) {
    if (hasMinDateTimeRequirements && minDateTime && enteredDateTime) {
      if (enteredDateTime < minDateTime) {
        return [{
          key: DateTimeValidationErrorMsg.MinDateTime,
          message: /*#__PURE__*/React.createElement(FormattedMessage, _extends({}, IntlValidationErrorMessages[DateTimeValidationErrorMsg.MinDateTime], {
            values: {
              mnd: new Date(convertDateFormatToSlashes(requirements.mnd || '')),
              mnt: new Date("01/01/1999 ".concat(requirements.mnt))
            }
          }))
        }];
      }
    }

    // Max Date and time
    if (hasMaxDateTimeRequirements && enteredDateTime && maxDateTime) {
      if (enteredDateTime > maxDateTime) {
        return [{
          key: DateTimeValidationErrorMsg.MaxDateTime,
          message: /*#__PURE__*/React.createElement(FormattedMessage, _extends({}, IntlValidationErrorMessages[DateTimeValidationErrorMsg.MaxDateTime], {
            values: {
              mxd: new Date(convertDateFormatToSlashes(requirements.mxd || '')),
              mxt: new Date("01/01/1999 ".concat(requirements.mxt))
            }
          }))
        }];
      }
    }
  } else {
    // Individual date and time validations

    var validationErrors = [];

    // Validate Date
    var dateValidationErrors = getDateValidation(requirements, dateTime.date);
    if (dateValidationErrors) {
      validationErrors.push.apply(validationErrors, _toConsumableArray(dateValidationErrors));
    }

    // Validate Time
    var timeValidationErrors = getTimeValidation(requirements, dateTime.time);
    if (timeValidationErrors) {
      validationErrors.push.apply(validationErrors, _toConsumableArray(timeValidationErrors));
    }
    if (validationErrors.length > 0) {
      return validationErrors;
    }
  }
};

// Helper functions for choice validation

var choiceIsSelected = function choiceIsSelected(selectedChoices, choiceId) {
  return selectedChoices.includes(choiceId) || selectedChoices.map(function (id) {
    return id.split(':')[0];
  }).includes(choiceId.toString());
};
export var getChoiceValidation = function getChoiceValidation(requirements, choices, choiceAnswers, asyncRequiredChoice, fieldLabel) {
  var numChoicesSelected = choiceAnswers.choices.length;
  // Validate required
  if (requirements.rq) {
    if (numChoicesSelected === 0) {
      return fieldLabel && fg('a11y_error_messages') ? [{
        key: ValidationErrorMsg.RequiredWithLabel,
        message: /*#__PURE__*/React.createElement(FormattedMessage, _extends({}, IntlValidationErrorMessages[ValidationErrorMsg.RequiredWithLabel], {
          values: {
            fieldLabel: fieldLabel
          }
        }))
      }] : [{
        key: ValidationErrorMsg.Required,
        message: /*#__PURE__*/React.createElement(FormattedMessage, IntlValidationErrorMessages[ValidationErrorMsg.Required])
      }];
    }
  }
  var validationErrors = [];
  if (numChoicesSelected !== 0) {
    if (requirements.mns && requirements.mns === requirements.mxs) {
      // Check for exact num of choices
      if (numChoicesSelected !== requirements.mns) {
        validationErrors.push({
          key: ChoiceValidationErrorMsg.ExactChoices,
          message: /*#__PURE__*/React.createElement(FormattedMessage, _extends({}, IntlValidationErrorMessages[ChoiceValidationErrorMsg.ExactChoices], {
            values: {
              mns: requirements.mns
            }
          }))
        });
      }
    } else if (requirements.mns && requirements.mxs) {
      // check for in between num of choices
      if (numChoicesSelected < requirements.mns || numChoicesSelected > requirements.mxs) {
        validationErrors.push({
          key: ChoiceValidationErrorMsg.BetweenChoices,
          message: /*#__PURE__*/React.createElement(FormattedMessage, _extends({}, IntlValidationErrorMessages[ChoiceValidationErrorMsg.BetweenChoices], {
            values: {
              mns: requirements.mns,
              mxs: requirements.mxs
            }
          }))
        });
      }
    } else {
      // min or max num of choices
      if (requirements.mns && numChoicesSelected < requirements.mns) {
        validationErrors.push({
          key: ChoiceValidationErrorMsg.MinChoices,
          message: /*#__PURE__*/React.createElement(FormattedMessage, _extends({}, IntlValidationErrorMessages[ChoiceValidationErrorMsg.MinChoices], {
            values: {
              mns: requirements.mns
            }
          }))
        });
      }
      if (requirements.mxs && numChoicesSelected > requirements.mxs) {
        validationErrors.push({
          key: ChoiceValidationErrorMsg.MaxChoices,
          message: /*#__PURE__*/React.createElement(FormattedMessage, _extends({}, IntlValidationErrorMessages[ChoiceValidationErrorMsg.MaxChoices], {
            values: {
              mxs: requirements.mxs
            }
          }))
        });
      }
    }
    if (requirements.ch) {
      var _requiredChoice$child;
      // check for required choice
      var requiredChoice = asyncRequiredChoice !== null && asyncRequiredChoice !== void 0 ? asyncRequiredChoice : choices.find(function (choice) {
        return choice.id === requirements.ch || requirements.ch && choice.id === requirements.ch.split(':')[0];
      });
      // check for required child choice
      var requiredChildChoice = requiredChoice === null || requiredChoice === void 0 || (_requiredChoice$child = requiredChoice.children) === null || _requiredChoice$child === void 0 ? void 0 : _requiredChoice$child.find(function (child) {
        var _requirements$ch;
        return child.id === ((_requirements$ch = requirements.ch) === null || _requirements$ch === void 0 ? void 0 : _requirements$ch.split(':')[1]);
      });
      if (requiredChoice && !choiceIsSelected(choiceAnswers.choices, requirements.ch) && (!requirements.ch.includes(':') || requiredChildChoice)) {
        validationErrors.push({
          key: ChoiceValidationErrorMsg.ReqChoice,
          message: /*#__PURE__*/React.createElement(FormattedMessage, _extends({}, IntlValidationErrorMessages[ChoiceValidationErrorMsg.ReqChoice], {
            values: {
              label: requiredChildChoice ? "".concat(requiredChoice.label, " - ").concat(requiredChildChoice.label) : requiredChoice.label
            }
          }))
        });
      }
    }
  }
  if (validationErrors.length > 0) {
    return validationErrors;
  }
};
export var getUserPickerValidation = function getUserPickerValidation(requirements, userPickerAnswers, fieldLabel) {
  var numChoicesSelected = userPickerAnswers.length;
  if (requirements.rq) {
    if (numChoicesSelected === 0) {
      return fieldLabel && fg('a11y_error_messages') ? [{
        key: ValidationErrorMsg.RequiredWithLabel,
        message: /*#__PURE__*/React.createElement(FormattedMessage, _extends({}, IntlValidationErrorMessages[ValidationErrorMsg.RequiredWithLabel], {
          values: {
            fieldLabel: fieldLabel
          }
        }))
      }] : [{
        key: ValidationErrorMsg.Required,
        message: /*#__PURE__*/React.createElement(FormattedMessage, IntlValidationErrorMessages[ValidationErrorMsg.Required])
      }];
    }
  }
  var validationErrors = [];
  if (requirements.mns && requirements.mns === requirements.mxs) {
    // Check for exact num of choices
    if (numChoicesSelected !== requirements.mns) {
      validationErrors.push({
        key: UserPickerValidationErrorMsg.ExactChoices,
        message: /*#__PURE__*/React.createElement(FormattedMessage, _extends({}, IntlValidationErrorMessages[UserPickerValidationErrorMsg.ExactChoices], {
          values: {
            mns: requirements.mns
          }
        }))
      });
    }
  } else if (requirements.mns && requirements.mxs) {
    // check for in between num of choices
    if (numChoicesSelected < requirements.mns || numChoicesSelected > requirements.mxs) {
      validationErrors.push({
        key: UserPickerValidationErrorMsg.BetweenChoices,
        message: /*#__PURE__*/React.createElement(FormattedMessage, _extends({}, IntlValidationErrorMessages[UserPickerValidationErrorMsg.BetweenChoices], {
          values: {
            mns: requirements.mns,
            mxs: requirements.mxs
          }
        }))
      });
    }
  } else {
    // min or max num of choices
    if (requirements.mns && numChoicesSelected < requirements.mns) {
      validationErrors.push({
        key: UserPickerValidationErrorMsg.MinChoices,
        message: /*#__PURE__*/React.createElement(FormattedMessage, _extends({}, IntlValidationErrorMessages[UserPickerValidationErrorMsg.MinChoices], {
          values: {
            mns: requirements.mns
          }
        }))
      });
    }
    if (requirements.mxs && numChoicesSelected > requirements.mxs) {
      validationErrors.push({
        key: UserPickerValidationErrorMsg.MaxChoices,
        message: /*#__PURE__*/React.createElement(FormattedMessage, _extends({}, IntlValidationErrorMessages[UserPickerValidationErrorMsg.MaxChoices], {
          values: {
            mxs: requirements.mxs
          }
        }))
      });
    }
  }
  if (validationErrors.length > 0) {
    return validationErrors;
  }
};
export var getAttachmentValidation = function getAttachmentValidation(requirements, files, questionType, fieldLabel) {
  if (requirements.rq && questionType === 'at') {
    if (files.length === 0) {
      return fieldLabel && fg('a11y_error_messages') ? [{
        key: ValidationErrorMsg.RequiredWithLabel,
        message: /*#__PURE__*/React.createElement(FormattedMessage, _extends({}, IntlValidationErrorMessages[ValidationErrorMsg.RequiredWithLabel], {
          values: {
            fieldLabel: fieldLabel
          }
        }))
      }] : [{
        key: ValidationErrorMsg.Required,
        message: /*#__PURE__*/React.createElement(FormattedMessage, IntlValidationErrorMessages[ValidationErrorMsg.Required])
      }];
    }
  }
};